"use client"

import { useEffect, useRef, useState } from "react"
import "./Dashboard.css"
import "./travailler-client.css"

const translations = {
  fr: {
    back: "Retour",
    pageTitle: "Travailler",
    pageSubtitle: "Espaces de travail et services pour vos journées à l’hôtel",
    coworking: "Co-working",
    hotelServices: "Services de l'hôtel",
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    availableAtReception: "Réseau Accor",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
    languageMenu: "Choisir la langue",
  },
  en: {
    back: "Back",
    pageTitle: "Work",
    pageSubtitle: "Workspaces and services for your stay",
    coworking: "Co-working",
    hotelServices: "Hotel services",
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow us",
    availableAtReception: "accor network",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
    languageMenu: "Choose language",
  },
  ar: {
    back: "رجوع",
    pageTitle: "العمل",
    pageSubtitle: "مساحات عمل وخدمات خلال إقامتك في الفندق",
    coworking: "مساحة العمل المشترك",
    hotelServices: "خدمات الفندق",
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
    languageMenu: "اختر اللغة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const TravaillerClient = () => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("IBIS-language")
      if (saved && translations[saved]) return saved
    }
    return "fr"
  })
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const languageSelectorRef = useRef(null)

  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  useEffect(() => {
    document.documentElement.dir = currentLanguage === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = currentLanguage
  }, [currentLanguage])

  useEffect(() => {
    if (!showLanguageDropdown) return
    const handleDown = (e) => {
      if (languageSelectorRef.current && !languageSelectorRef.current.contains(e.target)) {
        setShowLanguageDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleDown)
    return () => document.removeEventListener("mousedown", handleDown)
  }, [showLanguageDropdown])

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  const cards = [
    {
      id: "coworking",
      titleKey: "coworking",
      path: "/coworking-client",
      image: "/images/co-work.jpeg",
    },
    {
      id: "services",
      titleKey: "hotelServices",
      path: "/services-hotel-client",
      image: "/images/informations.webp",
    },
  ]

  return (
    <div className={`travailler-client-page ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <header className="app-header">
        <div className="header-left">
          <button
            type="button"
            className="header-back-link"
            onClick={() => {
              window.location.href = "/home"
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path
                d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("back")}
          </button>
        </div>
        <div className="logo-container">
          <img src="/images/logo2.webp" alt="ibis" className="logo" />
        </div>
        <div className="header-right">
          <div className="language-selector" ref={languageSelectorRef}>
            <button
              type="button"
              className="language-toggle"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              aria-expanded={showLanguageDropdown}
              aria-haspopup="listbox"
              aria-label={t("languageMenu")}
            >
              <img
                src={getCurrentLanguage()?.flag || "/placeholder.svg"}
                alt=""
                className="language-flag"
              />
              <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
            </button>
            {showLanguageDropdown && (
              <div className="language-dropdown" role="listbox">
                {languages.map((lang) => (
                  <div
                    key={lang.code}
                    className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      changeLanguage(lang.code)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") changeLanguage(lang.code)
                    }}
                  >
                    <img src={lang.flag || "/placeholder.svg"} alt="" className="flag-small" />
                    <span>{lang.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <header className="travailler-intro">
          <h1 className="travailler-intro-title">{t("pageTitle")}</h1>
          <p className="travailler-intro-subtitle">{t("pageSubtitle")}</p>
        </header>

        <div className="travailler-cards">
          {cards.map((card, index) => (
            <a
              key={card.id}
              href={card.url || card.path}
              className="travailler-card"
              aria-label={t(card.titleKey)}
              style={{ animationDelay: `${index * 75}ms` }}
              {...(card.url ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              <div className="travailler-card-media">
                <div
                  className="travailler-card-image"
                  style={{ backgroundImage: `url("${card.image}")` }}
                  aria-hidden={true}
                />
                <div className="travailler-card-image-overlay" aria-hidden={true} />
              </div>
              <div className="travailler-card-label">
                <span className="travailler-card-label-text">{t(card.titleKey)}</span>
                <span className="travailler-card-arrow" aria-hidden={true}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 18l6-6-6-6"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </a>
          ))}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
            <p>{t("availableAtReception")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibisTunis" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/ibistunis/" aria-label="Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img src="/images/itbafa_logo_white.png" alt="" style={{ height: "18px", verticalAlign: "middle" }} />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default TravaillerClient
